import { useCallback, useEffect, useState, type FC } from 'react';
import RegisterLayout from '@/registration/component/Register/Layout';
import { UiSpinner, UiText, UiVStack } from '@/lib/ui';
import { isError, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { type ApiResponseSingle } from '@/api/tenantClient';
import { loadOrder, type Order, orderQueryKey } from '@/api/registration';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { useTenantApi } from '@/account/hook/useTenantApi';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useRegisterGroupSteps } from '@/registration/hook/useRegisterGroupSteps';
import { useGroupRegistrationQuery } from '@/registration/hook/useGroupRegistrationQuery';

const PAID = 'Paid';
const STATUS_APPROVED = 'Approved';
const PaymentProcessing: FC = () => {
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { toNextStepWithSteps, toNextStep } = useRegisterGroupSteps();
  const [searchParams] = useSearchParams();
  const { eventId } = useRegisterRoute();
  const groupRegistrationQuery = useGroupRegistrationQuery(eventId);
  const orderHashParam = searchParams.get('orderHash') ?? groupRegistrationQuery?.data?.orderHash;
  const [isFetch, setIsFetch] = useState<boolean>(!!orderHashParam);

  const reloadAndToNextStep = useCallback(async () => {
    const data = await groupRegistrationQuery.refetch();
    const steps = data.data?.groupRegistrationSteps;
    if (steps) {
      toNextStepWithSteps(steps);
    } else {
      toNextStep();
    }
  }, [groupRegistrationQuery, toNextStepWithSteps, toNextStep]);

  const { data } = useQuery<ApiResponseSingle<Order>, Error>({
    queryKey: [orderQueryKey, { orderHashParam }],
    queryFn: async () => {
      return await loadOrder(createTenantApiRequest)(orderHashParam ?? '');
    },
    refetchInterval: (order, { state }) => {
      if (state.dataUpdateCount > 20) {
        // Refetch 15 times, stop and throw error
        setIsFetch(false);
        return false;
      }
      const status = order?.item?.status;
      if (isError(order?.errors)) {
        setIsFetch(false);
        return false;
      } else if (status === STATUS_APPROVED || status === PAID) {
        return false;
      }
      return 1500;
    },
    enabled: !!orderHashParam && !isApiPreparing
  });

  useEffect(
    () => {
      const status = data?.item?.status;
      if (status === STATUS_APPROVED || status === PAID) {
        void reloadAndToNextStep();
      }
    },
    [data?.item?.status, reloadAndToNextStep]
  );

  if (isFetch) {
    return (
      <RegisterLayout header={<></>}>
        <UiVStack spacing={4}>
          <UiSpinner />
          <UiText variant={'h6'}>We&apos;re completing your order, do not close this page.</UiText>
        </UiVStack>
      </RegisterLayout>
    );
  }

  return (
    <RegisterLayout header={<></>}>
      <BaseMessageBarError>
        Something went wrong. To prevent potential duplicate payments please avoid attempting to make payment again, but contact support instead.
      </BaseMessageBarError>
    </RegisterLayout>
  );
};

export default PaymentProcessing;
